<template>
  <div class="fuel-consult">
    <div class="nav-bar-operations">
      <img
        src="@/assets/logogruas.png"
        alt="HomePage"
        class="logo-button"
        @click="$router.push({ path: '/combustibles' })"
      />
    </div>

    <div class="shell">
      <header class="header">
        <div>
          <p class="eyebrow">Combustibles</p>
          <h1>Consultar cargas</h1>
          <p class="sub">Facturas y cargas individuales de combustible</p>
        </div>
      </header>

      <nav class="tabs" role="tablist">
        <button
          :class="['tab', { 'tab--active': tab === 'invoices' }]"
          role="tab"
          @click="switchTab('invoices')"
        >
          Facturas
        </button>
        <button
          :class="['tab', { 'tab--active': tab === 'refills' }]"
          role="tab"
          @click="switchTab('refills')"
        >
          Cargas
        </button>
      </nav>

      <!-- ============================= Invoices tab ============================= -->
      <section v-if="tab === 'invoices'" class="panel">
        <div class="panel__head">
          <div>
            <h2>Facturas</h2>
            <p class="muted">{{ invoices.rows.length }} / {{ invoices.count }}</p>
          </div>
        </div>

        <div class="filters-bar">
          <label class="field">
            <span>Proveedor</span>
            <select v-model="invoices.filters.providerId" @change="fetchInvoices">
              <option :value="null">Todos</option>
              <option v-for="p in providers" :key="p.id" :value="p.id">{{ p.name }}</option>
            </select>
          </label>
          <label class="field">
            <span>Desde</span>
            <input v-model="invoices.filters.dateFrom" type="date" @change="fetchInvoices" />
          </label>
          <label class="field">
            <span>Hasta</span>
            <input v-model="invoices.filters.dateTo" type="date" @change="fetchInvoices" />
          </label>
          <label class="field field--grow">
            <span>Buscar facturas</span>
            <input
              v-model="invoices.filters.search"
              type="text"
              placeholder="Factura o proveedor"
              autocomplete="off"
              @input="debouncedFetchInvoices"
            />
          </label>
        </div>

        <div v-if="invoices.loading" class="empty muted">Cargando facturas…</div>
        <div v-else-if="!invoices.rows.length" class="empty muted">No hay facturas con esos filtros.</div>
        <ul v-else class="invoice-list">
          <li
            v-for="inv in invoices.rows"
            :key="inv.id"
            class="invoice-row"
            role="link"
            tabindex="0"
            @click="viewInvoiceRefills(inv)"
            @keyup.enter="viewInvoiceRefills(inv)"
          >
            <strong class="invoice-row__number">{{ inv.invoice_number }}</strong>
            <span class="invoice-row__meta">
              {{ inv.provider_name.toUpperCase() }}<template v-if="inv.invoice_date"> · {{ inv.invoice_date }}</template>
            </span>
            <span class="invoice-row__meta">
              <template v-if="inv.period_start || inv.period_end">
                DESDE: {{ inv.period_start || '?' }} · HASTA: {{ inv.period_end || '?' }}
              </template>
              <template v-else>SIN PERIODO</template>
            </span>
            <span :class="['status-chip', inv.status === 'completada' ? 'status-chip--ok' : 'status-chip--warn']">
              {{ inv.status === 'completada' ? 'COMPLETADA' : 'EN REVISIÓN' }}
            </span>
            <span class="invoice-row__meta">
              {{ inv.refill_count }} carga(s) ligada(s) · {{ formatMoney(inv.refill_total) }}
              <template v-if="inv.total_amount"> de {{ formatMoney(inv.total_amount) }}</template>
            </span>
            <span class="invoice-row__actions">
              <button
                class="icon-btn"
                title="Ver documento"
                :disabled="invoices.docLoadingId === inv.id"
                @click.stop="viewInvoiceDocument(inv)"
              >
                {{ invoices.docLoadingId === inv.id ? '…' : '📄' }}
              </button>
              <button class="icon-btn icon-btn--danger" title="Eliminar factura" @click.stop="deleteInvoice(inv)">
                🗑
              </button>
            </span>
          </li>
        </ul>
      </section>

      <!-- ============================= Refills tab ============================= -->
      <section v-else class="panel">
        <div class="panel__head">
          <div>
            <h2>Cargas</h2>
            <p class="muted">{{ refills.rows.length }} / {{ refills.count }}</p>
          </div>
        </div>

        <div v-if="refills.invoiceFilter" class="invoice-filter-banner">
          Mostrando cargas ligadas a la factura
          <strong>{{ refills.invoiceFilter.invoice_number }}</strong>
          ({{ refills.invoiceFilter.provider_name }})
          <button class="btn btn--small" @click="clearInvoiceFilter">Quitar filtro</button>
        </div>

        <div class="filters-bar">
          <label class="field">
            <span>Proveedor</span>
            <select v-model="refills.filters.providerId" @change="fetchRefills">
              <option :value="null">Todos</option>
              <option v-for="p in providers" :key="p.id" :value="p.id">{{ p.name }}</option>
            </select>
          </label>
          <label class="field">
            <span>Desde</span>
            <input v-model="refills.filters.dateFrom" type="date" @change="fetchRefills" />
          </label>
          <label class="field">
            <span>Hasta</span>
            <input v-model="refills.filters.dateTo" type="date" @change="fetchRefills" />
          </label>
          <label class="field field--grow">
            <span>Buscar cargas</span>
            <input
              v-model="refills.filters.search"
              type="text"
              placeholder="Unidad, operador, ticket o tarjeta"
              autocomplete="off"
              @input="debouncedFetchRefills"
            />
          </label>
        </div>

        <div v-if="refills.loading" class="empty muted">Cargando cargas…</div>
        <div v-else-if="!refills.rows.length" class="empty muted">No hay cargas con esos filtros.</div>
        <div v-else class="table-scroll">
          <table class="table">
            <thead>
              <tr>
                <th>Fecha</th>
                <th>Hora</th>
                <th>Unidad</th>
                <th>Est.</th>
                <th>Ticket</th>
                <th>Tarjeta</th>
                <th>Producto</th>
                <th class="num">Litros</th>
                <th class="num">Total</th>
                <th class="num">Km anterior</th>
                <th class="num">Kilometraje</th>
                <th class="num">Km recorridos</th>
                <th class="num">Km/L</th>
                <th>Operador</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="row in refills.rows" :key="row.id">
                <td>{{ row.date }}</td>
                <td>{{ row.time || '—' }}</td>
                <td>{{ row.unit_short_id || row.unit_raw || '—' }}</td>
                <td>
                  <span
                    :class="['est-cell', row.invoice_id ? 'est-cell--linked' : 'est-cell--unlinked']"
                    :title="row.invoice_id ? `Ligada a la factura ${row.invoice_number}` : 'Sin factura ligada'"
                  ></span>
                </td>
                <td>{{ row.ticket || '—' }}</td>
                <td>{{ row.card || '—' }}</td>
                <td>{{ row.product || '—' }}</td>
                <td class="num">{{ formatNumber(row.liters) }}</td>
                <td class="num">{{ formatMoney(row.total_cost) }}</td>
                <td class="num">{{ formatNumber(row.km_prev) }}</td>
                <td class="num">{{ formatNumber(row.odometer) }}</td>
                <td class="num">{{ formatNumber(row.km_run) }}</td>
                <td class="num">{{ row.km_per_liter ?? '—' }}</td>
                <td class="operator-cell">{{ row.operator_name || row.operator_raw || '—' }}</td>
                <td class="actions-cell">
                  <button
                    class="icon-btn"
                    :disabled="!row.invoice_id"
                    :title="row.invoice_id ? 'Ver la factura ligada' : 'Sin factura ligada'"
                    @click="viewRefillInvoice(row)"
                  >
                    →
                  </button>
                  <button class="icon-btn icon-btn--danger" title="Eliminar carga" @click="deleteRefill(row)">
                    🗑
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import Swal from 'sweetalert2'

export default {
  name: 'FuelConsult',
  data() {
    return {
      tab: 'invoices',
      providers: [],
      invoices: {
        rows: [],
        count: 0,
        loading: false,
        docLoadingId: null,
        filters: { providerId: null, dateFrom: '', dateTo: '', search: '' },
      },
      refills: {
        rows: [],
        count: 0,
        loading: false,
        invoiceFilter: null,
        filters: { providerId: null, dateFrom: '', dateTo: '', search: '' },
      },
      searchTimer: null,
    }
  },
  created() {
    this.fetchProviders()
    this.fetchInvoices()
  },
  methods: {
    authHeaders() {
      return { Authorization: 'Token ' + localStorage.getItem('authToken') }
    },
    formatNumber(value) {
      if (value === null || value === undefined || value === '') return '—'
      const num = parseFloat(value)
      if (Number.isNaN(num)) return '—'
      return num.toLocaleString('es-MX', { maximumFractionDigits: 3 })
    },
    formatMoney(value) {
      if (value === null || value === undefined || value === '') return '—'
      const num = parseFloat(value)
      if (Number.isNaN(num)) return '—'
      return num.toLocaleString('es-MX', { style: 'currency', currency: 'MXN' })
    },
    switchTab(tab) {
      this.tab = tab
      if (tab === 'invoices') this.fetchInvoices()
      else this.fetchRefills()
    },
    async fetchProviders() {
      try {
        const { data } = await axios.get(`${process.env.API_DIRECTORY}/fuel/providers`, {
          headers: this.authHeaders(),
        })
        this.providers = data.results || []
      } catch (error) {
        this.providers = []
      }
    },

    // ----------------------------- Invoices -----------------------------
    debouncedFetchInvoices() {
      clearTimeout(this.searchTimer)
      this.searchTimer = setTimeout(this.fetchInvoices, 300)
    },
    async fetchInvoices() {
      this.invoices.loading = true
      try {
        const filters = this.invoices.filters
        const { data } = await axios.get(`${process.env.API_DIRECTORY}/fuel/invoices`, {
          headers: this.authHeaders(),
          params: {
            provider_id: filters.providerId || undefined,
            date_from: filters.dateFrom || undefined,
            date_to: filters.dateTo || undefined,
            search: filters.search || undefined,
          },
        })
        this.invoices.rows = data.results || []
        this.invoices.count = data.count ?? this.invoices.rows.length
      } catch (error) {
        Swal.fire('Error', 'No se pudieron cargar las facturas.', 'error')
      } finally {
        this.invoices.loading = false
      }
    },
    viewInvoiceRefills(invoice) {
      this.refills.invoiceFilter = invoice
      this.refills.filters = { providerId: null, dateFrom: '', dateTo: '', search: '' }
      this.tab = 'refills'
      this.fetchRefills()
    },
    async viewInvoiceDocument(invoice) {
      this.invoices.docLoadingId = invoice.id
      try {
        const { data } = await axios.get(`${process.env.API_DIRECTORY}/get-files/`, {
          headers: this.authHeaders(),
          params: { object_id: invoice.id, object_type: 'fuel_invoice' },
        })
        const file = Array.isArray(data) ? data[0] : (data.results || [])[0] || data[0]
        if (!file || !file.content) {
          Swal.fire('Sin documento', 'Esta factura no tiene documento almacenado.', 'info')
          return
        }
        const filename = file.metadata?.filename || 'factura.pdf'
        const mime = filename.toLowerCase().endsWith('.pdf') ? 'application/pdf' : 'application/octet-stream'
        const bytes = Uint8Array.from(atob(file.content), (c) => c.charCodeAt(0))
        const url = URL.createObjectURL(new Blob([bytes], { type: mime }))
        window.open(url, '_blank')
      } catch (error) {
        Swal.fire('Error', 'No se pudo obtener el documento.', 'error')
      } finally {
        this.invoices.docLoadingId = null
      }
    },
    async deleteInvoice(invoice) {
      const confirm = await Swal.fire({
        title: '¿Eliminar factura?',
        text:
          `Se eliminará la factura ${invoice.invoice_number} y su documento. ` +
          `Las ${invoice.refill_count} carga(s) ligada(s) se conservan pero quedarán sin factura.`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Eliminar',
        cancelButtonText: 'Cancelar',
        confirmButtonColor: '#dc2626',
      })
      if (!confirm.isConfirmed) return
      try {
        await axios.delete(`${process.env.API_DIRECTORY}/fuel/invoices/${invoice.id}`, {
          headers: this.authHeaders(),
        })
        this.fetchInvoices()
      } catch (error) {
        Swal.fire('Error', 'No se pudo eliminar la factura.', 'error')
      }
    },

    // ----------------------------- Refills -----------------------------
    debouncedFetchRefills() {
      clearTimeout(this.searchTimer)
      this.searchTimer = setTimeout(this.fetchRefills, 300)
    },
    async fetchRefills() {
      this.refills.loading = true
      try {
        const filters = this.refills.filters
        const { data } = await axios.get(`${process.env.API_DIRECTORY}/fuel/refills`, {
          headers: this.authHeaders(),
          params: {
            invoice_id: this.refills.invoiceFilter?.id || undefined,
            provider_id: filters.providerId || undefined,
            date_from: filters.dateFrom || undefined,
            date_to: filters.dateTo || undefined,
            search: filters.search || undefined,
          },
        })
        this.refills.rows = data.results || []
        this.refills.count = data.count ?? this.refills.rows.length
      } catch (error) {
        Swal.fire('Error', 'No se pudieron cargar las cargas.', 'error')
      } finally {
        this.refills.loading = false
      }
    },
    clearInvoiceFilter() {
      this.refills.invoiceFilter = null
      this.fetchRefills()
    },
    viewRefillInvoice(row) {
      if (!row.invoice_id) return
      this.refills.invoiceFilter = {
        id: row.invoice_id,
        invoice_number: row.invoice_number,
        provider_name: row.provider_name,
      }
      this.fetchRefills()
    },
    async deleteRefill(row) {
      const confirm = await Swal.fire({
        title: '¿Eliminar carga?',
        text: `Se eliminará la carga del ${row.date} (${formatRefillLabel(row)}).`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Eliminar',
        cancelButtonText: 'Cancelar',
        confirmButtonColor: '#dc2626',
      })
      if (!confirm.isConfirmed) return
      try {
        await axios.delete(`${process.env.API_DIRECTORY}/fuel/refills/${row.id}`, {
          headers: this.authHeaders(),
        })
        this.fetchRefills()
      } catch (error) {
        Swal.fire('Error', 'No se pudo eliminar la carga.', 'error')
      }
    },
  },
}

function formatRefillLabel(row) {
  const unit = row.unit_short_id || row.unit_raw || 'sin unidad'
  return `${unit}, ${row.liters} L`
}
</script>

<style scoped>
.fuel-consult {
  --bg: #f7f8fb;
  --muted: #667085;
  --text: #0f172a;
  --brand: #ffc434;
  --primary: #2563eb;
  --line: rgba(16, 24, 40, 0.12);
  --ok-bg: rgba(22, 163, 74, 0.14);
  --ok-text: #15803d;
  --warn-bg: #fff7e6;
  --warn-border: #f5c76f;
  --warn-text: #8a5a00;
  min-height: 100vh;
  background: var(--bg);
  padding: 12vh 24px 48px;
}

.nav-bar-operations {
  position: absolute;
  top: 0;
  left: 0;
  width: 100vw;
  height: 10vh;
  background-color: var(--brand);
  z-index: 1000;
  display: flex;
  align-items: center;
  padding-left: 16px;
  box-shadow: 0 2px 0 rgba(0, 0, 0, 0.05);
}
.logo-button {
  height: 55%;
  object-fit: contain;
  cursor: pointer;
}

.shell {
  max-width: 1280px;
  margin: 0 auto;
}

.header {
  margin-top: 12px;
}
.eyebrow {
  margin: 0;
  color: var(--muted);
  font-size: 0.74rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}
.header h1 {
  margin: 2px 0 0;
  font-size: 1.4rem;
  color: var(--text);
}
.sub {
  margin: 4px 0 0;
  color: var(--muted);
}

.tabs {
  display: flex;
  gap: 8px;
  margin-top: 18px;
  border-bottom: 1px solid var(--line);
}
.tab {
  border: none;
  background: transparent;
  padding: 10px 16px;
  font-size: 0.95rem;
  font-weight: 600;
  color: var(--muted);
  cursor: pointer;
  border-bottom: 3px solid transparent;
}
.tab--active {
  color: var(--primary);
  border-bottom-color: var(--primary);
}

.panel {
  margin-top: 18px;
  background: #fff;
  border: 1px solid var(--line);
  border-radius: 16px;
  padding: 18px;
  box-shadow: 0 6px 18px rgba(15, 23, 42, 0.06);
}
.panel__head h2 {
  margin: 0;
  font-size: 1.05rem;
  color: var(--text);
}
.muted {
  color: var(--muted);
  font-size: 0.9rem;
  margin: 4px 0 0;
}
.empty {
  padding: 36px 0;
  text-align: center;
}

.filters-bar {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  gap: 12px;
  margin: 12px 0 16px;
  padding: 12px;
  border: 1px solid var(--line);
  border-radius: 12px;
  background: #fbfcff;
}
.field {
  display: flex;
  flex-direction: column;
  gap: 5px;
  font-size: 0.82rem;
  color: var(--muted);
  min-width: 160px;
}
.field--grow {
  flex: 1;
  min-width: 220px;
}
.field input,
.field select {
  padding: 8px 10px;
  border: 1px solid var(--line);
  border-radius: 10px;
  font-size: 0.9rem;
  background: #fff;
  color: var(--text);
}

/* Invoice list */
.invoice-list {
  list-style: none;
  margin: 0;
  padding: 0;
  display: grid;
  gap: 8px;
}
.invoice-row {
  display: grid;
  grid-template-columns: 110px 1.1fr 1.3fr 120px 1.4fr auto;
  align-items: center;
  gap: 12px;
  padding: 11px 14px;
  border: 1px solid var(--line);
  border-radius: 12px;
  background: #fff;
  cursor: pointer;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
}
.invoice-row:hover {
  border-color: rgba(37, 99, 235, 0.45);
  box-shadow: 0 4px 12px rgba(15, 23, 42, 0.08);
}
.invoice-row__number {
  color: var(--text);
  font-size: 0.92rem;
}
.invoice-row__meta {
  color: var(--muted);
  font-size: 0.8rem;
}
.invoice-row__actions {
  display: flex;
  gap: 6px;
}

.status-chip {
  justify-self: start;
  padding: 3px 10px;
  border-radius: 6px;
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.03em;
  white-space: nowrap;
}
.status-chip--ok {
  background: var(--ok-bg);
  color: var(--ok-text);
}
.status-chip--warn {
  background: var(--warn-bg);
  color: var(--warn-text);
  border: 1px solid var(--warn-border);
}

.invoice-filter-banner {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  margin: 10px 0;
  padding: 10px 12px;
  border: 1px solid rgba(37, 99, 235, 0.35);
  background: rgba(37, 99, 235, 0.07);
  border-radius: 10px;
  color: var(--text);
  font-size: 0.9rem;
}

/* Refills table */
.table-scroll {
  overflow-x: auto;
}
.table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.85rem;
}
.table th,
.table td {
  text-align: left;
  padding: 8px 9px;
  border-bottom: 1px solid var(--line);
  color: var(--text);
  white-space: nowrap;
}
.table thead th {
  color: var(--muted);
  font-size: 0.72rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}
.table .num {
  text-align: right;
}
.operator-cell {
  max-width: 220px;
  overflow: hidden;
  text-overflow: ellipsis;
}
.actions-cell {
  display: flex;
  gap: 6px;
}

.est-cell {
  display: inline-block;
  width: 34px;
  height: 18px;
  border-radius: 4px;
}
.est-cell--linked {
  background: #22c55e;
}
.est-cell--unlinked {
  background: #facc15;
}

.btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 9px 14px;
  border-radius: 12px;
  border: 1px solid var(--line);
  background: #fff;
  color: var(--text);
  font-weight: 600;
  cursor: pointer;
}
.btn--small {
  padding: 4px 10px;
  font-size: 0.8rem;
  border-radius: 8px;
}

.icon-btn {
  border: 1px solid var(--line);
  background: #fff;
  border-radius: 8px;
  padding: 4px 8px;
  font-size: 0.85rem;
  cursor: pointer;
  color: var(--muted);
}
.icon-btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}
.icon-btn--danger {
  color: #b91c1c;
}
.icon-btn--danger:hover {
  background: rgba(185, 28, 28, 0.08);
}

@media (max-width: 920px) {
  .invoice-row {
    grid-template-columns: 1fr 1fr;
  }
}

@media screen and (max-height: 586px) {
  .nav-bar-operations {
    height: 10vh;
  }
  .fuel-consult {
    padding-top: 12vh;
  }
}
</style>
